import { connect } from 'react-redux'
import { rem } from 'polished'
import cn from 'clsx'
import debounce from 'lodash/debounce'
import moment from 'moment'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import OfferList, { OfferListAction, OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import {
  convertDateRangesToMonthString,
  createSearchSessionKey,
  decodeSearchParams,
  deserialiseOccupancy,
  encodeOfferIds,
  encodeSearchParams,
  getDistanceEqFilter,
  getDistanceLteFilter,
  getSearchKey,
  getSearchTypeFromFilters,
  getsScheduleVisibilitiesFilter,
  mapFlexibleNightsToDurationRange,
} from 'lib/search/searchUtils'

import { productClickViewDetails } from 'actions/AnalyticsActions'
import { replaceWithRegion } from 'actions/NavigationActions'
import { searchResultDealClick, searchSessionDealClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import Pane from 'components/Common/Pane'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import type { SearchFormRef } from 'components/Search'
import SearchResultsMap from 'components/Search/SearchResultsMap/SearchResultsMap'
import { replace } from 'connected-react-router'
import { HOTEL_SEARCH_TYPES, OFFER_TYPE_BED_BANK, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_VILLA } from 'constants/offer'
import {
  FLEXIBLE_DURATION_RANGE,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
  LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL_NATURAL_LANGUAGE,
  LOCATION_SEARCH_INPUT_TITLE_LABEL,
  LOCATION_SEARCH_INPUT_TITLE_LABEL_NATURAL_LANGUAGE,
} from 'constants/search'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import {
  GLOBAL_SEARCH_INITIAL_STATE,
  GlobalSearchAction,
  GlobalSearchStateActions,
} from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import useOfferList, { TrackingOptions } from 'hooks/Offers/useOfferList'
import useQueryParams from 'hooks/useQueryParams'
import useStableObjectReference from 'hooks/useStableObjectReference'
import {
  parseSearchString,
  setSearchParamValue,
  validateBoundsSearchParams,
  validateDestinationSearchParams,
  validateLandmarkSearchParams, validateNaturalLanguageSearchParams,
  validatePropertySearchParams,
} from 'lib/url/searchUrlUtils'
import noop from 'lib/function/noop'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import HotelSearchHeader from './HotelSearchHeader'
import config from 'constants/config'
import getOfferListKey from 'lib/offer/offerListKey'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchPopularHotelFilters } from 'actions/OfferActions'
import SearchResultsMapOfferView from 'components/Search/SearchResultsMap/SearchResultsMapOfferView'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { bezierCurve } from 'styles/tools/constants'
import zIndex from 'styles/tools/z-index'
import useLastNonEmptyValue from 'hooks/useLastNonEmptyValue'
import { EmptyArray } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import {
  OfferListEventHandler,
  OfferListEvents,
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import OfferListPagination from 'components/OfferList/OfferListPagination'
import { pluralizeToString } from 'lib/string/pluralize'
import LayoutContainer from 'components/Common/LayoutContainer'
import HotelSearchSplitViewNoResults from './HotelSearchSplitViewNoResults'
import Group from 'components/utils/Group'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import OfferCountAndSort from './OfferCountAndSort'
import usePrevious from 'hooks/usePrevious'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import { isAnyOffer } from 'lib/offer/offerTypes'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import RefreshSearchModal from 'components/SearchV2/Components/Mobile/RefreshSearchModal'
import ModalContext from 'contexts/ModalContext'
import useStreamSearchExpired from 'hooks/Offers/useStreamSearchExpired'
import { addRecentSearch } from 'actions/SearchActions'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import {
  mapGlobalSearchContextToSnowplowSearchEvent,
  mapOfferListFiltersToFilterContexts, mapOfferListFiltersToSortContext,
} from 'analytics/mapSnowplowSearchTracking'
import { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import useSortWasApplied from 'hooks/Search/useSortWasApplied'
import useFilterWasApplied from 'hooks/Search/useFilterWasApplied'
import useTrackingSearchEvent from 'hooks/Search/useTrackingSearchEvent'
import getObjectKey from 'lib/object/getObjectKey'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from '../../../hooks/Optimizely/useOptimizelyExperiment'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import useSearchSession from '../../../hooks/Search/useSearchSession'

const ANALYTICS_HOTEL_SEARCH_TAG = 'HS'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  max-height: 100vh;
`

const MapWrapper = styled(CSSBreakpoint)`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
`

const OfferContainer = styled.div`
  position: absolute;
  display: flex;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: ${zIndex.min};
  pointer-events: none;
  transform: translateY(100%);
  transition: transform .3s ${bezierCurve};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  &.show {
    pointer-events: auto;
    transform: translateY(0);
  }

  ${mediaQueryUp.tablet} {
    justify-content: center;
  }
`

const OfferListContainer = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  position: absolute;
  left: 0;
  top: 0;
  overflow-y: scroll;
  height: 100%;
  width: clamp(${rem(300)}, 60%, ${rem(1000)});
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding: ${rem(16)} ${rem(24)};
  transition: transform 0.3s ease-in-out;
`

const SplitViewColumns = styled.div`
  display: grid;
  grid-template-columns: clamp(${rem(300)}, 60%, ${rem(1000)}) 1fr;
  grid-template-areas: ". map";
  transition: grid-template-columns 0.3s ease-in-out;
  overflow: auto;
  min-height: 100%;

  &.hideListView {
    grid-template-columns: 0px 1fr;

    > ${OfferListContainer} {
      transform: translateX(-100%);
    }
  }
`

const MapViewContainer = styled.div`
  grid-area: map;
`

const ToggleMapScreenButton = styled(IconButton)`
  position: absolute;
  z-index: 1;
  grid-area: map;
  margin: ${rem(16)};
  box-shadow: ${(props) => props.theme.shadow.bottom.medium};
`

interface Props {
  filters: App.OfferListFilters
  windowSearch: string
  isLoggedIn: boolean
  regionCode: string
  offerListKey: string
  searchSessionId: string
  replaceWithRegion: Utils.ActionDispatcher<typeof replaceWithRegion>
  productClickViewDetails: Utils.ActionDispatcher<typeof productClickViewDetails>
}

type SelectedOfferIds = Array<{
  id: string
}>

function decodeOfferIds(idsParam?: string): SelectedOfferIds {
  if (!idsParam) return []

  return idsParam.split(',').map(id => ({
    id: id.includes(':') ? id.split(':')[1] : id,
  }))
}

function HotelSearchMapPage(props: Props) {
  const {
    filters,
    windowSearch,
    searchSessionId: deprecatedSearchSessionId,
    replaceWithRegion,
    productClickViewDetails,
  } = props
  const params = parseSearchString(windowSearch)
  const [previousDestination, setPreviousDestination] = useState<string | undefined>()
  const [paginatedOfferList, setPaginatedOfferList] = useState<App.OfferList | undefined>()
  const [page, setPage] = useState(0)
  const [fullScreenMap, setFullScreenMap] = useState(false)
  const [zeroResultsBanner, setZeroResultsBanner] = useState(false)
  const [userHasMoved, setUserHasMoved] = useState<boolean>(false)
  const [numberOfSearchesPerformed, setNumberOfSearchesPerformed] = useState<number>(0)
  const [selectedFilters, setSelectedFilters] = useState<App.SelectedFilters | undefined>(undefined)
  const rootRef = useRef<HTMLDivElement>(null)
  const dispatch = useAppDispatch()
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const naturalLanguageSearch = !!useOptimizelyExperiment(OptimizelyExperiments.searchNaturalLanguageSearch)

  const pageSize = config.search.mapView.pinLimit ?? 20

  useEffect(() => {
    if (!windowSearch.includes('bounds') && (windowSearch.includes('destination') || windowSearch.includes('propertyName'))) {
      setPreviousDestination(windowSearch)
    }
  }, [windowSearch])

  const previousFilters = usePrevious(filters)

  // Only increment numberOfSearchesPerformed when filters (excluding bounds) actually change
  useEffect(() => {
    if (!previousFilters) return

    const locationChanged = !!((filters.propertyId || filters.destinationId) && (previousFilters.propertyId !== filters.propertyId ||
      previousFilters.destinationId !== filters.destinationId))

    const roomsChanged = getObjectKey({ rooms: previousFilters.rooms }) !== getObjectKey({ rooms: filters.rooms })
    const checkInChanged = previousFilters.checkIn !== filters.checkIn
    const checkOutChanged = previousFilters.checkOut !== filters.checkOut
    const flexibleMonthsChanged = previousFilters.flexibleMonths !== filters.flexibleMonths
    const flexibleNightsChanged = previousFilters.flexibleNights !== filters.flexibleNights
    const meaningfulFiltersChanged =
      roomsChanged ||
      checkInChanged ||
      checkOutChanged ||
      flexibleMonthsChanged ||
      flexibleNightsChanged

    if (!meaningfulFiltersChanged && !locationChanged) return

    setNumberOfSearchesPerformed(prev => prev + 1)
  }, [filters, previousFilters])

  // Get currently selected offer IDs
  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(params.selectedOfferIds).map(({ id }) => id)
  ), [params.selectedOfferIds])

  const overridePaginationOfferList = useMemo<OfferListAndAction | undefined>(() => {
    if (paginatedOfferList) {
      return {
        action: OfferListAction.OVERRIDE,
        offerList: paginatedOfferList,
      }
    }
  }, [paginatedOfferList])

  const anyOffersSelected = selectedOfferIds.length > 0

  const lastSelectedOfferIds = useLastNonEmptyValue(selectedOfferIds) ?? EmptyArray

  // Handle adding selected offer IDs to URL
  const setSelectedOffersURLParams = useCallback(
    (offers: Array<App.Offer | App.BedbankOffer | App.OfferListMetaData>) => {
      const newParams = new URLSearchParams(windowSearch)
      if (offers.length > 0) {
        newParams.set('selectedOfferIds', encodeOfferIds(offers))
      } else {
        newParams.delete('selectedOfferIds')
      }
      const newSearchString = newParams.toString()
      if (newSearchString !== windowSearch) {
        const currentHash = window.location.hash
        dispatch(replace({
          pathname: window.location.pathname,
          search: newSearchString,
          hash: currentHash,
          state: window.history.state
        }))
      }
    },
    [dispatch, windowSearch],
  )

  // Handle deselecting offers
  const onDeselectOffers = useCallback(() => {
    setSelectedOffersURLParams([])
  }, [setSelectedOffersURLParams])

  // decodeSearchParams can change depending on the current date, so it's not enough to
  // just use useMemo with windowSearch as the dependency.
  // Better to evaluate on each render and then stabilise it with useStableObjectReference.
  const searchParams = useStableObjectReference(decodeSearchParams(windowSearch))

  const dates = useMemo(() => ({
    checkIn: searchParams.dates.checkIn,
    checkOut: searchParams.dates.checkOut,
  }), [searchParams])
  const rooms = searchParams.rooms

  const [overrideFetching, setOverrideFetching] = useState<boolean>(false)

  const resetLoadingState = useMemo(
    () => debounce(() => setOverrideFetching(false), 750),
    [],
  )

  useEffect(() => {
    return () => resetLoadingState.cancel()
  }, [resetLoadingState])

  // We want to delay showing the offer list for 750ms to avoid flickering
  const handleOfferListLoading = useCallback(() => {
    setOverrideFetching(true)
    resetLoadingState()
  }, [resetLoadingState])

  const onBoundsChanged = useCallback((newBounds: google.maps.LatLngBounds) => {
    if (!newBounds) { return }
    handleOfferListLoading()
    setPage(0)
    setUserHasMoved(true)

    const searchItem: App.SearchItem = {
      searchType: 'bounds',
      value: newBounds.toUrlValue(),
      format: {
        mainText: 'Search area',
      },
    }

    const searchStr = encodeSearchParams({
      urlSearch: windowSearch,
      searchItem,
      dates,
      rooms,
      filters,
      flexibleNights: filters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: filters.flexibleMonths,
      userSelectedFlexibleMonths: true,
      selectedOfferIds,
    }).toString()
    replaceWithRegion('/search/map', searchStr)
  }, [handleOfferListLoading, windowSearch, dates, rooms, filters, selectedOfferIds, replaceWithRegion])

  const formRef = useRef<SearchFormRef>(null)
  const offerListContainerRef = useRef<HTMLDivElement>(null)

  const searchType = getSearchTypeFromFilters(filters)

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current?.openDatePickerMenu()
  }, [])

  // Handle errors and zero results
  const [modal, setModal] = useState<'error' | 'offline' | 'zeroResults' | null>(null)
  const clearModal = useCallback(() => setModal(null), [])

  const onError = useCallback(() => {
    if (typeof window.navigator.onLine === 'boolean' && !window.navigator.onLine) {
      setModal('offline')
    } else {
      setModal('error')
    }
  }, [])

  const invalidSearchParams = useMemo(() =>
    !validateDestinationSearchParams(params) &&
    !validateLandmarkSearchParams(params) &&
    !validatePropertySearchParams(params) &&
    !validateBoundsSearchParams(params) &&
    !validateNaturalLanguageSearchParams(params),
  [params])

  const handleProductClick = useCallback((offer: App.Offer | App.OfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    addGTMEvent(searchResultDealClick())
    addGTMEvent(searchSessionDealClick(deprecatedSearchSessionId, offer.id))
    productClickViewDetails(offer)
  }, [productClickViewDetails, deprecatedSearchSessionId])

  const searchTargetLabel = (params.landmarkName || params.destinationName)

  const queryParams = useQueryParams()
  const searchKey = getSearchKey(params)
  const { searchId, searchEvent: searchSessionSearchEvent } = useSearchEvent(searchKey)

  const searchSessionKey = createSearchSessionKey({ destinationId: params.destinationId, propertyId: params.propertyId, landmarkId: params.landmarkId })
  const { searchSessionId } = useSearchSession(searchSessionKey)

  const globalHotelsSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)
  const isMobileScreen = useScreenSizeOnly('mobile')

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem, mobileSearchParams } = buildSelectDatesSearchParams(offer)

    if (isMobileScreen) {
      toggleSearchVertical('hotels', {
        withDateSelect: true,
        search: mobileSearchParams,
      })
    } else {
      if (globalHotelsSearchDispatchRef.current) {
        globalHotelsSearchDispatchRef.current({
          type: GlobalSearchStateActions.SET_SEARCH_ITEM,
          searchItem: propertySearchItem,
        })
      }

      formRef.current?.form.current.scrollIntoView()
      formRef.current?.openDatePickerMenu()
    }
  }, [isMobileScreen, toggleSearchVertical])

  const utmBp = useAppSelector(state => state.utm.bp)

  const isAnytimeSearch = (!filters?.checkIn && !filters?.checkOut) && !filters?.flexibleNights &&
    !(selectedFilters?.travelDates || selectedFilters?.flexibleTravelDates)
  const hideAnytimePricesEnabled = !!utmBp && isAnytimeSearch

  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType,
    onBedbankSelectDatesPrompt,
    searchTargetLabel,
    onAnyHotelSelectDatesPrompt,
    eventAnalytics: { searchId },
    hideAnytimePricesEnabled,
  }, queryParams)

  useEffect(() => {
    globalHotelsSearchDispatchRef.current = globalHotelsSearchDispatch
  }, [globalHotelsSearchDispatch])

  useEffect(() => {
    globalHotelsSearchDispatch({
      type: GlobalSearchStateActions.SET_HIDE_ANYTIME_PRICES_ENABLED,
      hideAnytimePricesEnabled,
    })
  }, [hideAnytimePricesEnabled, globalHotelsSearchDispatch])

  const mapPinCount = 20

  const mapPinLimit = config.ENABLE_MAP_SEARCH_SHOW_ALL_FLASH ? undefined : mapPinCount ?? config.search.mapView.pinLimit + config.search.mapView.additionalPins
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)

  const sortByGeographicDiversity = !!useOptimizelyExperiment(OptimizelyExperiments.searchSortByGeographicDiversity, !filters.sortBy)

  const rawFilters = useMemo((): App.OfferListFilters => {
    const offerTypes: Array<App.OfferListOfferType> = [...HOTEL_SEARCH_TYPES, OFFER_TYPE_BUNDLE_AND_SAVE, OFFER_TYPE_BED_BANK]
    if (config.ENABLE_VILLA_RENTALS) {
      offerTypes.push(OFFER_TYPE_VILLA)
    }

    return {
      ...filters,
      offerTypes,
      sortBy: sortByGeographicDiversity ? 'geographicDiversity' : 'recommended',
      distanceEq: getDistanceEqFilter(searchType),
      distanceLte: getDistanceLteFilter(searchType),
      limit: mapPinLimit,
      scheduleVisibilities: getsScheduleVisibilitiesFilter(isSpoofed),
      shouldUsePersonalizedSearch: false,
      isStream: true,
    }
  }, [filters, sortByGeographicDiversity, searchType, mapPinLimit, isSpoofed])

  const prevNaturalLanguageQuery = usePrevious(rawFilters.naturalLanguageQuery)

  const listFilters = useMemo<App.OfferListFilters>(() => {
    // When there is a new natural language query, use the raw filters
    // as the selected filters are not set yet
    if (prevNaturalLanguageQuery !== rawFilters.naturalLanguageQuery) {
      return rawFilters
    }

    return {
      ...rawFilters,
      occupancies: selectedFilters?.occupancy
        ? deserialiseOccupancy(selectedFilters.occupancy)
        : rawFilters.rooms,
      propertyId: selectedFilters?.propertyId ?? rawFilters.propertyId,
      destinationId: selectedFilters?.placeIds?.[0] ?? rawFilters.destinationId, // nls supports multiple places however filters only support one
      flexibleNights: selectedFilters?.flexibleTravelDates?.numbersOfNights
        ? mapFlexibleNightsToDurationRange(selectedFilters.flexibleTravelDates.numbersOfNights)
        : rawFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: selectedFilters?.flexibleTravelDates?.dateRanges
        ? convertDateRangesToMonthString(selectedFilters.flexibleTravelDates.dateRanges)
        : rawFilters.flexibleMonths,
      durationRange: rawFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkIn: selectedFilters?.travelDates?.checkIn ?? rawFilters.checkIn,
      checkOut: selectedFilters?.travelDates?.checkOut ?? rawFilters.checkOut,
      amenities: selectedFilters?.amenities?.length ? selectedFilters.amenities : rawFilters.amenities,
      inclusions: selectedFilters?.inclusions?.length ? selectedFilters.inclusions : rawFilters.inclusions,
      rawFiltersKey: getOfferListKey(rawFilters),
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rawFilters,
    selectedFilters?.flexibleTravelDates?.dateRanges,
    selectedFilters?.flexibleTravelDates?.numbersOfNights,
    selectedFilters?.occupancy,
    selectedFilters?.placeIds,
    selectedFilters?.propertyId,
    selectedFilters?.travelDates?.checkIn,
    selectedFilters?.travelDates?.checkOut,
    selectedFilters?.amenities,
    selectedFilters?.inclusions,
  ])

  const overrideDestinationIds = useMemo<Array<string> | undefined>(() => {
    if (selectedFilters?.propertyId) {
      return [selectedFilters.propertyId]
    }
    if (selectedFilters?.placeIds) {
      return selectedFilters.placeIds
    }
    return undefined
  }, [selectedFilters])

  const overrideDestinationType = useMemo<'location' | 'property' | undefined>(() => {
    if (selectedFilters?.propertyId) {
      return 'property'
    }
    if (selectedFilters?.placeIds) {
      return 'location'
    }
    return undefined
  }, [selectedFilters])

  /**
   * Tracking options for the offer list. Used to generate SearchEvent
   */
  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      occupancies: selectedFilters?.occupancy
        ? deserialiseOccupancy(selectedFilters.occupancy)
        : globalHotelsSearchState.occupancies,
      flexibleNights: selectedFilters?.flexibleTravelDates?.numbersOfNights
        ? mapFlexibleNightsToDurationRange(selectedFilters.flexibleTravelDates.numbersOfNights)
        : rawFilters.flexibleNights as FLEXIBLE_DURATION_RANGE,
      flexibleMonths: selectedFilters?.flexibleTravelDates?.dateRanges
        ? convertDateRangesToMonthString(selectedFilters.flexibleTravelDates.dateRanges)
        : rawFilters.flexibleMonths,
      durationRange: rawFilters.durationRange as Array<FLEXIBLE_DURATION_RANGE>,
      checkinDate: selectedFilters?.travelDates?.checkIn ?
          moment(selectedFilters.travelDates.checkIn) :
          (rawFilters.checkIn ? moment(rawFilters.checkIn) : undefined),
      checkoutDate: selectedFilters?.travelDates?.checkOut ?
          moment(selectedFilters.travelDates.checkOut) :
          (rawFilters.checkOut ? moment(rawFilters.checkOut) : undefined),
      searchItem: searchParams?.searchItem,
      overrideDestinationIds,
      overrideDestinationType,
      eventAnalytics: { searchId },
    })
  }, [
    globalHotelsSearchState,
    selectedFilters?.occupancy,
    selectedFilters?.flexibleTravelDates?.numbersOfNights,
    selectedFilters?.flexibleTravelDates?.dateRanges,
    selectedFilters?.travelDates?.checkIn,
    selectedFilters?.travelDates?.checkOut,
    rawFilters.flexibleNights,
    rawFilters.flexibleMonths,
    rawFilters.durationRange,
    rawFilters.checkIn,
    rawFilters.checkOut,
    searchParams?.searchItem,
    searchId,
    overrideDestinationIds,
    overrideDestinationType,
  ])

  const filterContexts = useMemo(() => {
    return mapOfferListFiltersToFilterContexts({ offerListFilters: listFilters })
  }, [listFilters])

  const sortContext = useMemo(() => {
    return mapOfferListFiltersToSortContext({ offerListFilters: listFilters })
  }, [listFilters])

  const sortWasApplied = useSortWasApplied(listFilters)

  const filterWasApplied = useFilterWasApplied(listFilters)

  const trackingSearchEvent = useTrackingSearchEvent({
    searchSessionSearchEvent, sortWasApplied, filterWasApplied
  })

  const trackingOptions = useMemo<TrackingOptions>(() => ({
    searchEvent: trackingSearchEvent,
    searchContext,
    filterContexts,
    sortContext,
    searchSessionId,
    disable: !!(filters.bounds && userHasMoved && !filterWasApplied && !sortWasApplied) ||
      !!(filters.naturalLanguageQuery && !selectedFilters),
  }), [trackingSearchEvent, searchContext, filterContexts, sortContext, searchSessionId, filters.bounds, filters.naturalLanguageQuery, userHasMoved, filterWasApplied, sortWasApplied, selectedFilters])

  const minRequiredOffers = useMemo(() => {
    // I need this plus one because the map page pagination is zero indexed based
    return ((page + 1) * pageSize) || undefined
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const offerList = useOfferList(listFilters, {
    minRequiredOffers,
  }, trackingOptions)

  useEffect(() => {
    setSelectedFilters(offerList.selectedFilters)
  }, [offerList.selectedFilters])

  const onZeroResults = useCallback(() => {
    if (offerList.offerCount === 0 && !offerList.fetching) {
      setModal('zeroResults')
    }
  }, [offerList.fetching, offerList.offerCount])

  const previousOfferListKey = usePrevious(offerList.key)

  useEffect(() => {
    if (previousOfferListKey !== offerList.key) {
      setPage(0)
      offerListContainerRef.current?.scrollTo({ top: 0, behavior: 'instant' })
    }

    const paginatedOfferIds = offerList.offerIds.slice(page * pageSize, (page + 1) * pageSize)
    setPaginatedOfferList({
      ...offerList,
      offerIds: paginatedOfferIds,
      key: `${offerList.key}-page-${page}`,
    })
    setZeroResultsBanner(offerList.offerCount === 0 && !offerList.fetching)
  }, [offerList, page, pageSize, previousOfferListKey])

  const offerListFiltersProps = useMemo(() => {
    const listViewSearch = previousDestination || windowSearch
    return {
      searchType,
      switchView: {
        label: 'List view',
        view: 'list' as const,
        URL: `/search${listViewSearch}`,
        goBack: numberOfSearchesPerformed === 0,
      },
      listFilters,
    }
  }, [previousDestination, windowSearch, searchType, listFilters, numberOfSearchesPerformed])

  const tracking = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Map Offer List'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  const trackingClick = useOfferListTracking(
    getListName('OfferList', ANALYTICS_HOTEL_SEARCH_TAG, 'Search Page Map Pins'),
    {
      snowplow: {
        enabled: false,
      },
      key: getOfferListKey(listFilters),
    },
  )

  useEffect(() => {
    const { searchItem, dates: specificDates, rooms, flexibleSearch } = searchParams
    if (searchItem && ['destination', 'property'].includes(searchType)) {
      const dates = {
        checkIn: specificDates.checkIn,
        checkOut: specificDates.checkOut,
        flexibleMonths: flexibleSearch.flexibleMonths,
        flexibleDuration: flexibleSearch.flexibleNights,
        isAnytime: !(specificDates.checkIn || specificDates.checkOut || flexibleSearch.flexibleMonths || flexibleSearch.flexibleNights),
      }
      dispatch(addRecentSearch(searchItem, dates, rooms))
    }
  }, [searchParams, searchType, dispatch])

  const handleHover = useCallback((id: string, type: string, options?: { persist?: boolean }) => {
    const event = new CustomEvent('offerHover', { detail: { id, type, persist: options?.persist ?? false } })
    rootRef.current?.dispatchEvent(event)
  }, [])

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      const { offer } = dispatchAction
      addGTMEvent(searchResultDealClick())
      addGTMEvent(searchSessionDealClick(deprecatedSearchSessionId, offer.id))
      productClickViewDetails(offer)
    }
    if (dispatchAction.type === OfferListEvents.productHover) {
      const { offer } = dispatchAction
      if (isAnyOffer(offer)) {
        handleHover(offer?.id, offer?.type, { persist: true })
      }
    }
  }, [handleHover, productClickViewDetails, deprecatedSearchSessionId])

  useEffect(() => {
    dispatch(fetchPopularHotelFilters())
  }, [dispatch])

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
    dispatch(replace({ search: setSearchParamValue(windowSearch, 'pages', Math.max(newPage, 1)) }))
  }, [dispatch, windowSearch])

  const handleMapScreenToggle = useCallback(() => {
    setFullScreenMap(!fullScreenMap)
  }, [fullScreenMap])

  const showModal = useContext(ModalContext)
  const searchExpired = useStreamSearchExpired(listFilters)

  useEffect(() => {
    if (searchExpired) {
      showModal(<RefreshSearchModal />)
    }
  }, [searchExpired, showModal])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  if (invalidSearchParams) {
    return null
  }

  const isFlexibleSearch = !!(filters.flexibleMonths && filters.flexibleNights)
  const countText = isFlexibleSearch ?
    `${pluralizeToString('offer', offerList.offerCount ?? 0)} found with cheapest availabilities for your dates` :
    `${pluralizeToString('offer', offerList.offerCount ?? 0)} found`

  const showNoResults = (offerList.offerCount === 0 && !offerList.fetching) ||
    offerList.error === 'naturalLanguageSearchError'

  const offerListExtras: Array<OfferListV2Item> = [
    {
      id: 'no-results',
      position: 'start',
      element: <HotelSearchSplitViewNoResults />,
      show: showNoResults,
    },
  ]

  return (
    <GlobalSearchContextProvider
      state={globalHotelsSearchState}
      dispatch={globalHotelsSearchDispatch}
    >
      <Root ref={rootRef}>
        <Pane shadow="bottom-small">
          <HotelSearchHeader
            filters={listFilters}
            formRef={formRef}
            offerListFiltersProps={offerListFiltersProps}
            searchVertical="hotels"
            baseSearchPath="/search"
            baseSearchMapPath="/search/map"
            onFilterChange={handleFilterChange}
            onSortChange={noop}
            locationSearchPlaceholder={naturalLanguageSearch ? LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL_NATURAL_LANGUAGE : LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL}
            label={naturalLanguageSearch ? LOCATION_SEARCH_INPUT_TITLE_LABEL_NATURAL_LANGUAGE : LOCATION_SEARCH_INPUT_TITLE_LABEL}
            showNaturalLanguageSearchSuggestions={naturalLanguageSearch}
          />
        </Pane>
        <MapWrapper min="largeDesktop">
          <SplitViewColumns className={cn({ hideListView: fullScreenMap })}>
            <OfferListContainer ref={offerListContainerRef}>
              <div>
                {!showNoResults && <OfferCountAndSort
                  mainListTotal={offerList.offerCount ?? 0}
                  isFlexible={isFlexibleSearch}
                  filters={filters}
                  fetching={offerList.fetching}
                  windowSearch={windowSearch}
                  countTextOverride={countText}
                  onFilterChange={handleFilterChange}
                  onSortChange={noop}
                />}
              </div>
              <Group direction="vertical" gap={32}>
                <Group direction="vertical" gap={16}>
                  <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
                    <OfferList
                      extraOfferListAndAction={overridePaginationOfferList}
                      overrideFetching={overrideFetching}
                      filters={listFilters}
                      tileStyle="search"
                      eagerLoadFirstImage
                      defaultPage={
                        params.pages ? parseInt(params.pages) : 1
                      }
                      pageSize={10}
                      interstitials={offerListExtras}
                      disableUseOfferList
                      showLoadMoreTiles={false}
                    />
                  </OfferListEventsProvider>
                </Group>
                {!showNoResults && <OfferListPagination
                  offerListRef={offerListContainerRef}
                  page={page}
                  total={offerList.offerCount ?? 0}
                  itemsPerPage={pageSize}
                  handlePageChange={handlePageChange}
                />}
              </Group>
            </OfferListContainer>
            <MapViewContainer>
              <ToggleMapScreenButton
                kind="primary"
                variant="ghost"
                size="large"
                shape="square"
                onClick={handleMapScreenToggle}
              >
                {fullScreenMap ? (
                  <LineAngleRightIcon />
                ) : (
                  <LineAngleLeftIcon />
                )}
              </ToggleMapScreenButton>
              <OfferListEventsProvider tracking={trackingClick}>
                <SearchResultsMap
                  filters={listFilters}
                  onBoundsChanged={onBoundsChanged}
                  onSelectOffers={setSelectedOffersURLParams}
                  onDeselectOffers={onDeselectOffers}
                  selectedOfferIds={selectedOfferIds}
                  onError={onError}
                  onZeroResults={onZeroResults}
                  handleProductClick={handleProductClick}
                  onHover={handleHover}
                  rootRef={rootRef}
                  offerListOverride={paginatedOfferList}
                  pageSize={pageSize}
                  offerList={offerList}
                  fullScreenMap={fullScreenMap}
                  zeroResultsBanner={zeroResultsBanner}
                />
              </OfferListEventsProvider>
            </MapViewContainer>
          </SplitViewColumns>
        </MapWrapper>
        <MapWrapper max="desktop">
          <OfferListEventsProvider tracking={trackingClick}>
            <SearchResultsMap
              filters={listFilters}
              onBoundsChanged={onBoundsChanged}
              onSelectOffers={setSelectedOffersURLParams}
              onDeselectOffers={onDeselectOffers}
              selectedOfferIds={selectedOfferIds}
              onError={onError}
              onZeroResults={onZeroResults}
              handleProductClick={handleProductClick}
              onHover={handleHover}
              rootRef={rootRef}
              offerListOverride={paginatedOfferList}
              pageSize={pageSize}
              offerList={offerList}
              fullScreenMap={fullScreenMap}
              zeroResultsBanner={zeroResultsBanner}
            />
          </OfferListEventsProvider>
          <CSSBreakpoint max="mobile">
            <OfferContainer className={cn({ show: anyOffersSelected })}>
              <OfferListEventsProvider tracking={trackingClick}>
                <SearchResultsMapOfferView
                  key={JSON.stringify(lastSelectedOfferIds)}
                  offerIds={lastSelectedOfferIds}
                  filters={listFilters}
                  onProductClick={handleProductClick}
                  onDeselectOffers={onDeselectOffers}
                />
              </OfferListEventsProvider>
            </OfferContainer>
          </CSSBreakpoint>
        </MapWrapper>
      </Root>

      <Modal
        isOpen={modal === 'offline'}
        onClose={clearModal}
        title="Looks like you’re offline"
        primaryActionText="OK"
        onPrimaryActionClick={clearModal}
      >
        <BodyText variant="large">
          Connect to the internet to search this area.
        </BodyText>
      </Modal>
    </GlobalSearchContextProvider>
  )
}

const mapStateToProps = (state: App.State) => {
  const filters = getUrlOfferListFilters(state)
  const offerListKey = getOfferListKey(filters)

  return {
    windowSearch: state.router.location.search,
    filters,
    offerListKey,
    regionCode: state.geo.currentRegionCode,
    searchSessionId: state.analytics.search.sessions[offerListKey],
  }
}

const mapDispatchToProps = {
  replaceWithRegion,
  productClickViewDetails,
}

export default connect(mapStateToProps, mapDispatchToProps)(HotelSearchMapPage)
